// Package Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'
import { roleListQueryOptions } from '@/modules/role/role.hooks'

// Module Imports
import {
  adminUserDetailQueryOptions,
  adminUsersListQueryOptions,
} from '@/modules/admin/admin.hooks'
import { adminUsersSearchSchema } from '@/modules/admin/admin.schemas'

export const adminUsersRoute = createRoute({
  path: '/admins',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const adminUsersListRoute = createRoute({
  getParentRoute: () => adminUsersRoute,
  path: '/',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'admins.list',
      'Yönetici listesine erişim izniniz yok.'
    )
  },
  validateSearch: adminUsersSearchSchema,
  loader: async () => {
    await queryClient.ensureQueryData(adminUsersListQueryOptions())
  },
  head: () => ({
    meta: [{ title: 'Yönetici Listesi' }],
  }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/admin/pages/list-admin.page')
  ),
})

export const adminUsersCreateRoute = createRoute({
  getParentRoute: () => adminUsersRoute,
  path: 'create',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'admins.create',
      'Yönetici oluşturmak için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Alt Admin Oluştur' }],
  }),
  component: lazyRouteComponent(
    () => import('@/modules/admin/pages/create-admin.page')
  ),
})

export const adminUsersDetailRoute = createRoute({
  getParentRoute: () => adminUsersRoute,
  path: '$id',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'admins.detail',
      'Yönetici detayına erişim izniniz yok.'
    )
  },
  head: async ({ params: { id } }) => {
    const adminUser = await queryClient.fetchQuery(
      adminUserDetailQueryOptions(id)
    )
    return {
      meta: [{ title: `Alt Admin | ${adminUser.name}` }],
    }
  },
  loader: async ({ params: { id } }) => {
    await queryClient.ensureQueryData(adminUserDetailQueryOptions(id))
  },
  component: lazyRouteComponent(
    () => import('@/modules/admin/pages/admin-detail.page')
  ),
})

export const adminUsersEditRoute = createRoute({
  getParentRoute: () => adminUsersRoute,
  path: '$id/edit',
  beforeLoad: async ({ preload }) => {
    return routeHasPermission(
      preload,
      'admins.edit',
      'Yönetici düzenlemek için yetkiniz yok.'
    )
  },
  head: () => ({
    meta: [{ title: 'Yönetici Rollerini Düzenle' }],
  }),
  loader: async ({ params: { id } }) => {
    await Promise.all([
      queryClient.ensureQueryData(adminUserDetailQueryOptions(id)),
      queryClient.ensureQueryData(roleListQueryOptions()),
    ])
  },
  component: lazyRouteComponent(
    () => import('@/modules/admin/pages/edit-admin.page')
  ),
})
